import { useState, useCallback, useEffect, useMemo } from 'react'
import { useApiFetch } from '../../hooks/useApiFetch.js'
import { useAdminStore } from '../../stores/adminStore.js'
import { StatCard, Select, Input, Button, Table, Pagination, Alert, Badge, type TableColumn } from '../../components/ui/index.js'
import { StatusBadge } from '../../components/admin/index.js'
import { formatDate, formatNumber, formatPercent } from '../../lib/format.js'
import styles from './AdminAIGenerations.module.css'

interface AIGeneration {
  id: string
  style: string | null
  status: string
  modelId: string | null
  providerType: string | null
  cost: number | null
  creditsUsed: number | null
  isAdminTest: boolean
  createdAt: string
  userEmail: string
  userName: string | null
}

interface AIGenResponse {
  generations: AIGeneration[]
  pagination: { page: number; pageSize: number; total: number; totalPages: number }
  stats: { totalCount: number; totalCost: number; avgCost: number; failRate: number }
}

const STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'PENDING', label: 'PENDING' },
  { value: 'PROCESSING', label: 'PROCESSING' },
  { value: 'COMPLETED', label: 'COMPLETED' },
  { value: 'FAILED', label: 'FAILED' },
]

const STYLE_OPTIONS = [
  { value: '', label: '전체 스타일' },
  { value: 'CLASSIC_STUDIO', label: '클래식 스튜디오' },
  { value: 'OUTDOOR_GARDEN', label: '아웃도어 가든' },
  { value: 'SUNSET_BEACH', label: '선셋 비치' },
  { value: 'TRADITIONAL_HANBOK', label: '전통 한복' },
  { value: 'VINTAGE_CINEMATIC', label: '빈티지 시네마' },
  { value: 'LUXURY_HOTEL', label: '럭셔리 호텔' },
  { value: 'CITY_LIFESTYLE', label: '시티 라이프' },
  { value: 'ENCHANTED_FOREST', label: '숲속' },
  { value: 'BLACK_AND_WHITE', label: '흑백' },
  { value: 'MINIMALIST_GALLERY', label: '미니멀 갤러리' },
]

const MODEL_OPTIONS = [
  { value: '', label: '전체 모델' },
  { value: 'gpt-image-1', label: 'GPT Image 1' },
  { value: 'dall-e-3', label: 'DALL-E 3' },
  { value: 'gpt-image-1.5', label: 'GPT Image 1.5' },
  { value: 'gemini-flash-image', label: 'Gemini Flash' },
  { value: 'gemini-3-pro-image', label: 'Gemini 3 Pro' },
]

const ADMIN_TEST_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'true', label: '테스트만' },
  { value: 'false', label: '일반만' },
]

export function AdminAIGenerations() {
  const { filters, setFilters, resetFilters, selectedUserId, setSelectedUserId } = useAdminStore()
  const [statusFilter, setStatusFilter] = useState('')
  const [styleFilter, setStyleFilter] = useState('')
  const [modelFilter, setModelFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [adminTestFilter, setAdminTestFilter] = useState('')

  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  // 300ms debounce for search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(searchInput)
      setFilters({ page: 1 })
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput, setFilters])

  const params = useMemo(() => {
    const p = new URLSearchParams()
    p.set('page', String(filters.page))
    p.set('pageSize', String(filters.pageSize))
    p.set('sortBy', filters.sortBy)
    p.set('sortOrder', filters.sortOrder)
    if (statusFilter) p.set('status', statusFilter)
    if (styleFilter) p.set('style', styleFilter)
    if (modelFilter) p.set('modelId', modelFilter)
    if (selectedUserId) p.set('userId', selectedUserId)
    if (searchDebounced) p.set('search', searchDebounced)
    if (startDate) p.set('startDate', new Date(startDate).toISOString())
    if (endDate) p.set('endDate', new Date(endDate + 'T23:59:59').toISOString())
    if (adminTestFilter) p.set('isAdminTest', adminTestFilter)
    return p.toString()
  }, [filters, statusFilter, styleFilter, modelFilter, selectedUserId, searchDebounced, startDate, endDate, adminTestFilter])

  const { data, isLoading, error } = useApiFetch<AIGenResponse>(
    `/api/admin/ai-generations?${params}`
  )

  const handleSort = useCallback((key: string, order: 'asc' | 'desc') => {
    setFilters({ sortBy: key, sortOrder: order })
  }, [setFilters])

  const handleResetFilters = useCallback(() => {
    setStatusFilter('')
    setStyleFilter('')
    setModelFilter('')
    setSearchInput('')
    setSearchDebounced('')
    setStartDate('')
    setEndDate('')
    setAdminTestFilter('')
    setSelectedUserId(null)
    resetFilters()
  }, [resetFilters, setSelectedUserId])

  const columns: TableColumn<AIGeneration>[] = [
    { key: 'id', label: 'ID', render: (row) => row.id.slice(0, 8), width: '80px' },
    {
      key: 'userName',
      label: '유저',
      render: (row) => (
        <span>
          {row.userName ?? row.userEmail}
          {row.isAdminTest && <> <Badge color="yellow">테스트</Badge></>}
        </span>
      ),
    },
    { key: 'style', label: '스타일', render: (row) => row.style ?? '-' },
    { key: 'modelId', label: '모델', render: (row) => row.modelId ?? row.providerType ?? '-', width: '140px' },
    { key: 'status', label: '상태', sortable: true, render: (row) => <StatusBadge status={row.status} />, width: '100px' },
    { key: 'creditsUsed', label: '크레딧', sortable: true, render: (row) => row.creditsUsed != null ? String(row.creditsUsed) : '-', align: 'right' as const, width: '80px' },
    { key: 'cost', label: '비용($)', sortable: true, render: (row) => row.cost != null ? `$${row.cost.toFixed(4)}` : '-', align: 'right' as const, width: '90px' },
    { key: 'createdAt', label: '일시', sortable: true, render: (row) => formatDate(row.createdAt, 'yyyy.M.d HH:mm'), width: '130px' },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.statGrid}>
        <StatCard label="총 생성 수" value={formatNumber(data?.stats.totalCount ?? 0)} />
        <StatCard label="총 비용" value={`$${(data?.stats.totalCost ?? 0).toFixed(2)}`} />
        <StatCard label="평균 비용" value={`$${(data?.stats.avgCost ?? 0).toFixed(4)}`} />
        <StatCard label="실패율" value={formatPercent(data?.stats.failRate ?? 0)} />
      </div>

      <div className={styles.filterGrid}>
        <div className={styles.filterRow}>
          <Input
            size="sm"
            placeholder="유저 이메일/이름 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Select
            size="sm"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setFilters({ page: 1 }) }}
            options={STATUS_OPTIONS}
          />
          <Select
            size="sm"
            value={styleFilter}
            onChange={(e) => { setStyleFilter(e.target.value); setFilters({ page: 1 }) }}
            options={STYLE_OPTIONS}
          />
          <Select
            size="sm"
            value={modelFilter}
            onChange={(e) => { setModelFilter(e.target.value); setFilters({ page: 1 }) }}
            options={MODEL_OPTIONS}
          />
        </div>
        <div className={styles.filterRow}>
          <Input
            size="sm"
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setFilters({ page: 1 }) }}
            placeholder="시작일"
          />
          <Input
            size="sm"
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setFilters({ page: 1 }) }}
            placeholder="종료일"
          />
          <Select
            size="sm"
            value={adminTestFilter}
            onChange={(e) => { setAdminTestFilter(e.target.value); setFilters({ page: 1 }) }}
            options={ADMIN_TEST_OPTIONS}
          />
          <Button size="sm" variant="ghost" onClick={handleResetFilters}>
            필터 초기화
          </Button>
        </div>
      </div>

      {selectedUserId && (
        <div className={styles.userFilterTag}>
          <Badge color="blue">유저 필터: {selectedUserId.slice(0, 8)}...</Badge>
          <Button size="sm" variant="ghost" onClick={() => setSelectedUserId(null)}>
            해제
          </Button>
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      <Table<AIGeneration>
        columns={columns}
        data={data?.generations ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        sortBy={filters.sortBy}
        sortOrder={filters.sortOrder}
        onSort={handleSort}
      />

      {data?.pagination && data.pagination.totalPages > 1 && (
        <Pagination
          current={data.pagination.page}
          total={data.pagination.totalPages}
          onChange={(page) => setFilters({ page })}
          size="sm"
        />
      )}
    </div>
  )
}
